import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'chunk-reload';

/**
 * Drop-in replacement for React.lazy that survives stale deploys.
 *
 * After a redeploy, fingerprinted chunk hashes change and the old files are
 * deleted. A browser still holding the previous index.html will 404 on the
 * dynamic import ("Failed to fetch dynamically imported module"). When that
 * happens we force a single full reload to pull the fresh index.html + chunks.
 * A sessionStorage flag prevents an infinite reload loop if the failure is real
 * (e.g. genuinely offline) rather than a version skew.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Success — clear the guard so future skews can reload again.
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      const alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG);
      if (!alreadyReloaded) {
        window.sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        // Return a never-resolving promise so nothing renders before reload.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
